import { useRef, useState } from "react";
import { useDbClient } from "@tanstack/react-db";
import type { ForumComposePost } from "@softmax/web-ux/forum";
import { validatePostImage } from "@softmax/web-features/community";
import type { PostFeedScope } from "./post.collections";
import { createForumPost } from "./commands/post.mutations";
import { uploadPostImage } from "./commands/post-media.mutations";
import { writeError } from "./forum-presentation";

export function useForumPostCompose(scope: PostFeedScope) {
  const db = useDbClient();
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const postKey = useRef<{ fingerprint: string; key: string } | null>(null);
  const uploadedImage = useRef<{ file: File; s3Key: string } | null>(null);

  function submitPost({
    title,
    body,
    format,
    image,
  }: Parameters<ForumComposePost["onSubmit"]>[0]): Promise<boolean> {
    const imageError = image ? validatePostImage(image) : null;

    if (imageError) {
      setComposeError(imageError);
      return Promise.resolve(false);
    }

    setComposeBusy(true);
    setComposeError(null);
    let upload: Promise<string | undefined> = Promise.resolve(undefined);

    if (image) {
      upload =
        uploadedImage.current?.file === image
          ? Promise.resolve(uploadedImage.current.s3Key)
          : uploadPostImage({
              db,
              file: image,
              elevatedPrivileges: scope.elevatedPrivileges,
            }).then((s3Key) => {
              uploadedImage.current = { file: image, s3Key };
              return s3Key;
            });
    }

    return upload
      .then((imageKey) => {
        const fingerprint = JSON.stringify({ title, body, format, imageKey });
        const action =
          postKey.current?.fingerprint === fingerprint
            ? postKey.current
            : { fingerprint, key: crypto.randomUUID() };
        postKey.current = action;
        return createForumPost({
          db,
          scope,
          title,
          body,
          format,
          idempotencyKey: action.key,
          imageKey,
        });
      })
      .then(() => {
        postKey.current = null;
        uploadedImage.current = null;
        return true;
      })
      .catch((reason: unknown) => {
        setComposeError(writeError({ reason, fallback: "Post failed." }));
        return false;
      })
      .finally(() => setComposeBusy(false));
  }

  return { composeBusy, composeError, submitPost };
}
